interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  a: number;
  color: string;
  life: number;
  maxLife: number;
  ambient: boolean;
}

export type ParticleKind = 'received' | 'processed' | 'failed';

const PARTICLE_COLOR: Readonly<Record<ParticleKind, string>> = {
  received: 'rgba(255, 168, 74, ',
  processed: 'rgba(62, 232, 198, ',
  failed: 'rgba(255, 106, 106, ',
};

export class ParticleField {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly particles: Particle[] = [];
  private W = 0;
  private H = 0;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2d context unavailable');
    this.ctx = ctx;
    this.resize();
    window.addEventListener('resize', () => this.resize());
    for (let i = 0; i < 60; i++) this.particles.push(this.makeAmbient());
    requestAnimationFrame(() => this.frame());
  }

  spawn(kind: ParticleKind, intensity: number): void {
    const color = PARTICLE_COLOR[kind];
    const cx = this.W * (0.3 + Math.random() * 0.4);
    const cy = this.H * (0.3 + Math.random() * 0.3);
    const count = Math.floor(intensity * 22);
    for (let i = 0; i < count; i++) {
      const angle = ((Math.PI * 2) * i) / count + Math.random() * 0.3;
      const speed = 1.2 + Math.random() * 2.6;
      this.particles.push({
        x: cx,
        y: cy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        r: 1.4 + Math.random() * 2.4,
        a: 0.7,
        color,
        life: 0,
        maxLife: 60 + Math.random() * 40,
        ambient: false,
      });
    }
  }

  private makeAmbient(): Particle {
    return {
      x: Math.random() * this.W,
      y: Math.random() * this.H,
      vx: (Math.random() - 0.5) * 0.25,
      vy: (Math.random() - 0.5) * 0.25,
      r: 0.6 + Math.random() * 1.6,
      a: 0.06 + Math.random() * 0.14,
      color: 'rgba(180, 200, 255, ',
      life: 0,
      maxLife: Number.POSITIVE_INFINITY,
      ambient: true,
    };
  }

  private resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.W = this.canvas.width = window.innerWidth * dpr;
    this.H = this.canvas.height = window.innerHeight * dpr;
    this.canvas.style.width = `${window.innerWidth}px`;
    this.canvas.style.height = `${window.innerHeight}px`;
  }

  private frame(): void {
    this.ctx.clearRect(0, 0, this.W, this.H);
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      if (!p) continue;
      p.x += p.vx;
      p.y += p.vy;
      if (!p.ambient) {
        p.life += 1;
        p.vx *= 0.96;
        p.vy *= 0.96;
        const t = 1 - p.life / p.maxLife;
        const a = Math.max(0, p.a * t);
        this.ctx.beginPath();
        this.ctx.fillStyle = `${p.color}${a})`;
        this.ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        this.ctx.fill();
        if (p.life >= p.maxLife) this.particles.splice(i, 1);
      } else {
        if (p.x < 0) p.x = this.W;
        if (p.x > this.W) p.x = 0;
        if (p.y < 0) p.y = this.H;
        if (p.y > this.H) p.y = 0;
        this.ctx.beginPath();
        this.ctx.fillStyle = `${p.color}${p.a})`;
        this.ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        this.ctx.fill();
      }
    }
    requestAnimationFrame(() => this.frame());
  }
}
